import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  BookOpen, Loader2, ArrowRight, GraduationCap, DollarSign,
  Users, BarChart3, CalendarDays, AlertCircle, MessageCircle, Edit2
} from 'lucide-react';

const LEVELS = { beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' };
const STATUSES = { draft: 'مسودة', published: 'منشورة' };

export default function AdminCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Fetch course
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const snap = await getDoc(doc(db, 'courses', id));
        if (!snap.exists()) { setNotFound(true); }
        else {
          const data = snap.data();
          setCourse({
            id: snap.id, ...data,
            dateStr: data.createdAt?.toDate?.().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }) || '–'
          });
        }
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    fetchCourse();
  }, [id]);

  // Fetch enrolled students
  useEffect(() => {
    if (!id) return;
    const fetchStudents = async () => {
      setLoadingStudents(true);
      try {
        const enrollSnap = await getDocs(query(collection(db, 'enrollments'), where('courseId', '==', id)));
        const userIds = enrollSnap.docs.map(d => d.data().userId);

        if (userIds.length === 0) { setEnrolledStudents([]); setLoadingStudents(false); return; }

        // Fetch each user's data
        const studentPromises = userIds.map(async uid => {
          const userSnap = await getDoc(doc(db, 'users', uid));
          if (userSnap.exists()) {
            return { uid, ...userSnap.data() };
          }
          return { uid, name: 'مستخدم محذوف', nameAr: 'مستخدم محذوف' };
        });

        const students = await Promise.all(studentPromises);
        setEnrolledStudents(students);
      } catch (err) {
        console.error("Error fetching enrolled students:", err);
      } finally {
        setLoadingStudents(false);
      }
    };
    fetchStudents();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-slate-500">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
        <p className="font-bold">جاري تحميل تفاصيل الدورة...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-slate-500">
        <AlertCircle className="w-16 h-16 text-slate-300 dark:text-zinc-700 mb-4" />
        <h2 className="text-2xl font-bold mb-2">لم يتم العثور على الدورة</h2>
        <Link to="/admin/courses" className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-2xl font-bold hover:bg-primary-700 transition-all mt-4">
          <ArrowRight className="w-4 h-4" /> العودة لإدارة الدورات
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Back */}
      <Link to="/admin/courses" className="inline-flex items-center gap-2 text-slate-500 dark:text-zinc-400 hover:text-primary-600 font-bold mb-6 transition-colors group">
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        العودة لإدارة الدورات
      </Link>

      {/* Course Info Card */}
      <div className="bg-white dark:bg-zinc-900 rounded-3xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm mb-8">
        <div className="aspect-[1350/1080] max-h-80 bg-gradient-to-br from-primary-500 to-indigo-600 relative">
          {course.thumbnail ? (
            <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BookOpen className="w-24 h-24 text-white/20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {/* Status */}
          <div className={`absolute top-5 left-5 px-4 py-1.5 rounded-full text-mdfont-extrabold border ${course.status === 'published' ? 'bg-emerald-500/90 text-white border-emerald-400' : 'bg-slate-800/80 text-slate-200 border-slate-600'}`}>
            {STATUSES[course.status] || course.status}
          </div>
          <div className={`absolute top-5 right-5 px-4 py-1.5 rounded-full text-mdfont-extrabold backdrop-blur-sm border ${course.level === 'beginner' ? 'bg-blue-500/90 text-white border-blue-400/50'
            : course.level === 'intermediate' ? 'bg-amber-500/90 text-white border-amber-400/50'
              : 'bg-red-500/90 text-white border-red-400/50'}`}>
            {LEVELS[course.level] || course.level}
          </div>
        </div>

        <div className="p-8">
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-4">{course.title}</h1>

          <div className="flex flex-wrap items-center gap-6 text-mdtext-slate-500 dark:text-zinc-400 mb-6 pb-6 border-b border-slate-100 dark:border-white/5">
            <span className="flex items-center gap-2 font-bold">
              <div className="w-8 h-8 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg flex items-center justify-center"><DollarSign className="w-4 h-4 text-emerald-600" /></div>
              {course.price > 0 ? `$${course.price}` : 'مجاني'}
            </span>
            <span className="flex items-center gap-2 font-bold">
              <div className="w-8 h-8 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center"><Users className="w-4 h-4 text-blue-600" /></div>
              {course.studentsCount || 0} طالب
            </span>
            <span className="flex items-center gap-2 font-bold">
              <div className="w-8 h-8 bg-slate-50 dark:bg-white/5 rounded-lg flex items-center justify-center"><CalendarDays className="w-4 h-4 text-slate-400" /></div>
              {course.dateStr}
            </span>
          </div>

          {course.description && (
            <div>
              <h2 className="text-xl font-extrabold text-slate-900 dark:text-white mb-2">وصف الدورة</h2>
              <p className="text-slate-600 dark:text-zinc-300 leading-relaxed whitespace-pre-wrap">{course.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Enrolled Students */}
      <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-zinc-900/50 flex items-center justify-between">
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-primary-600" />
            الطلاب المسجلين
            <span className="bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 px-3 py-0.5 rounded-full text-mdfont-extrabold mr-2">
              {enrolledStudents.length}
            </span>
          </h2>
        </div>

        {loadingStudents ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600 mb-3" />
            <p className="font-bold text-md">جاري تحميل الطلاب...</p>
          </div>
        ) : enrolledStudents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Users className="w-12 h-12 text-slate-300 dark:text-zinc-700 mb-3" />
            <p className="font-bold">لا يوجد طلاب مسجلين بعد</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-zinc-900 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 text-mdfont-bold">
                  <th className="px-6 py-4">#</th>
                  <th className="px-6 py-4">الاسم (عربي)</th>
                  <th className="px-6 py-4">الاسم (إنجليزي)</th>
                  <th className="px-6 py-4">البريد الإلكتروني</th>
                  <th className="px-6 py-4">رقم الجوال</th>
                  <th className="px-6 py-4">المستوى</th>
                  <th className="px-6 py-4 text-center">تواصل</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {enrolledStudents.map((student, idx) => (
                  <tr key={student.uid} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-4 text-slate-400 font-bold">{idx + 1}</td>
                    <td className="px-6 py-4 font-bold text-slate-900 dark:text-white">{student.nameAr || student.name || '–'}</td>
                    <td className="px-6 py-4 text-slate-600 dark:text-zinc-300 font-sans" dir="ltr">{student.nameEn || '–'}</td>
                    <td className="px-6 py-4 text-slate-500 font-sans text-md" dir="ltr">{student.email || '–'}</td>
                    <td className="px-6 py-4 text-slate-500 font-sans" dir="ltr">{student.phone || '–'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-lg text-md font-bold ${student.designLevel === 'intermediate' ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400'
                        : student.designLevel === 'advanced' ? 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
                          : 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400'
                        }`}>
                        {LEVELS[student.designLevel] || 'مبتدئ'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {student.phone ? (
                        <a
                          href={`https://wa.me/${student.phone.replace(/\D/g, '')}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-xl font-bold text-mdtransition-colors"
                        >
                          <MessageCircle className="w-4 h-4" /> واتساب
                        </a>
                      ) : (
                        <span className="text-slate-300 dark:text-zinc-700">–</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
